import html2pdf, { Html2PdfInstance } from 'html2pdf.js';
import {
  ProgressIntervals,
  createProgressState,
  ensureMicroProgress,
  startContinuousProgress,
  startTransitionProgress,
  stopProgress,
} from './pdf-progress.utils';

interface GeneratePdfProps {
  pdfRef: React.RefObject<HTMLDivElement>;
  filename: string;
  onProgress?: (
    progress: number,
    totalSize: number,
    currentSize: number
  ) => void;
  abortController?: AbortController;
}

interface PdfConfigsProps {
  filename: string;
  includePageBreak?: boolean;
}

const getPdfConfigs = ({
  filename,
  includePageBreak = false,
}: PdfConfigsProps) => {
  const baseConfig = {
    margin: [10, 10, 35, 10],
    filename: `${filename}.pdf`,
    image: { type: 'jpeg', quality: 0.95 },
    html2canvas: {
      scale: 2,
      useCORS: true,
      letterRendering: true,
      logging: false,
      allowTaint: true,
      backgroundColor: null,
      imageTimeout: 0,
      removeContainer: true,
    },
    jsPDF: {
      unit: 'mm',
      format: 'a4',
      orientation: 'portrait',
      compress: true,
      optimization: true,
    },
  };

  if (includePageBreak) {
    baseConfig['pagebreak'] = {
      mode: ['css', 'legacy'],
      before: '.page-break-before',
      after: '.page-break-after',
      avoid: '.avoid-break',
    };
  }

  return baseConfig;
};

export const generatePdf = async ({
  pdfRef,
  filename,
  onProgress,
  abortController,
}: GeneratePdfProps): Promise<void> => {
  if (!pdfRef.current) {
    throw new Error('PDF reference is not available');
  }

  const initialEstimate = new Blob([pdfRef.current.outerHTML]).size;
  const progressState = createProgressState(initialEstimate);
  let intervals: ProgressIntervals;
  const currentIntervals: ProgressIntervals = {
    mainInterval: null,
    continuousInterval: null,
    transitionInterval: null,
    microProgressInterval: null,
  };

  let estimateWorker: Html2PdfInstance | null = null;
  let worker: Html2PdfInstance | null = null;

  try {
    // Initialize progress
    onProgress?.(0, 0, 0);
    progressState.isGenerating = true;

    // Add abort signal check
    if (abortController?.signal.aborted) {
      throw new Error('PDF generation cancelled');
    }

    // Start continuous micro-progress
    currentIntervals.microProgressInterval = ensureMicroProgress(
      progressState,
      100,
      onProgress
    );

    // Add abort signal check
    if (abortController?.signal.aborted) {
      throw new Error('PDF generation cancelled');
    }

    // Phase 1: Initial conversion (0-50%)
    intervals = {
      mainInterval: null,
      continuousInterval: null,
      transitionInterval: null,
      microProgressInterval: null,
    };
    intervals = startContinuousProgress(
      progressState,
      0,
      50,
      500,
      true,
      onProgress
    );
    Object.assign(currentIntervals, intervals);

    // First, create a worker to get the PDF blob and estimate size
    estimateWorker = html2pdf()
      .from(pdfRef.current)
      .set(getPdfConfigs({ filename }));

    // Get the PDF blob to estimate size
    const pdfBlob = await estimateWorker.output('blob');

    // Add abort signal check
    if (abortController?.signal.aborted) {
      throw new Error('PDF generation cancelled');
    }

    progressState.estimatedTotalSize = pdfBlob.size;

    // Clear previous intervals
    if (currentIntervals.mainInterval) {
      clearInterval(currentIntervals.mainInterval);
    }
    if (currentIntervals.continuousInterval) {
      clearInterval(currentIntervals.continuousInterval);
    }

    // Multiple transition phases for smoother progress
    // Transition 1: 50-55%
    currentIntervals.transitionInterval = startTransitionProgress(
      progressState,
      50,
      55,
      100,
      onProgress
    );
    await new Promise((resolve) => setTimeout(resolve, 100));

    // Transition 2: 55-60%
    currentIntervals.transitionInterval = startTransitionProgress(
      progressState,
      55,
      60,
      100,
      onProgress
    );
    await new Promise((resolve) => setTimeout(resolve, 100));

    // Phase 2: Main PDF Generation (60-90%)
    intervals = startContinuousProgress(
      progressState,
      60,
      90,
      800,
      true,
      onProgress
    );
    if (currentIntervals.transitionInterval) {
      clearInterval(currentIntervals.transitionInterval);
    }
    Object.assign(currentIntervals, intervals);

    // Create worker for actual PDF generation
    worker = html2pdf()
      .from(pdfRef.current)
      .set(getPdfConfigs({ filename, includePageBreak: true }));

    // Get the PDF instance
    const pdf = await worker.toPdf().get('pdf');

    // Add abort signal check
    if (abortController?.signal.aborted) {
      throw new Error('PDF generation cancelled');
    }

    const totalPages = pdf.internal.pages.length - 1;

    // Clear previous intervals
    if (currentIntervals.mainInterval) {
      clearInterval(currentIntervals.mainInterval);
    }
    if (currentIntervals.continuousInterval) {
      clearInterval(currentIntervals.continuousInterval);
    }

    // Multiple transition phases for footer preparation
    // Transition 1: 90-92%
    currentIntervals.transitionInterval = startTransitionProgress(
      progressState,
      90,
      92,
      50,
      onProgress
    );
    await new Promise((resolve) => setTimeout(resolve, 50));

    // Transition 2: 92-94%
    currentIntervals.transitionInterval = startTransitionProgress(
      progressState,
      92,
      94,
      50,
      onProgress
    );
    await new Promise((resolve) => setTimeout(resolve, 50));

    // Phase 3: Footer Generation (94-96%)
    intervals = startContinuousProgress(
      progressState,
      94,
      96,
      200,
      true,
      onProgress
    );
    if (currentIntervals.transitionInterval) {
      clearInterval(currentIntervals.transitionInterval);
    }
    Object.assign(currentIntervals, intervals);

    // Add footer to each page
    const addFooter = (pageNum: number) => {
      pdf.setPage(pageNum);
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      // Add footer line
      pdf.setDrawColor(229, 231, 235);
      pdf.line(10, pageHeight - 25, pageWidth - 10, pageHeight - 25);

      // Add copyright text
      pdf.setFontSize(10);
      pdf.setTextColor(107, 114, 128);
      const copyrightText = `© Lucius ${new Date().getFullYear()} - By Trustoo | Inspection du ${new Date().toLocaleDateString(
        'fr-FR',
        { day: 'numeric', month: 'long', year: 'numeric' }
      )}`;
      pdf.text(copyrightText, 10, pageHeight - 15);

      // Add page numbers
      const pageText = `Page ${pageNum} sur ${totalPages}`;
      const textWidth =
        (pdf.getStringUnitWidth(pageText) * 10) / pdf.internal.scaleFactor;
      pdf.text(pageText, pageWidth - textWidth - 10, pageHeight - 15);
    };

    // Process pages sequentially with progress updates
    for (let i = 1; i <= totalPages; i++) {
      // Add abort signal check before processing each page
      if (abortController?.signal.aborted) {
        throw new Error('PDF generation cancelled');
      }

      addFooter(i);
      // Update progress for each page processed
      const pageProgress = 96 + (i / totalPages) * 2;
      const currentSize = Math.floor(
        (progressState.estimatedTotalSize * pageProgress) / 100
      );
      onProgress?.(pageProgress, progressState.estimatedTotalSize, currentSize);
    }

    // Clear intervals
    if (currentIntervals.mainInterval) {
      clearInterval(currentIntervals.mainInterval);
    }
    if (currentIntervals.continuousInterval) {
      clearInterval(currentIntervals.continuousInterval);
    }

    // Multiple transition phases for final save
    // Transition 1: 98-98.5%
    currentIntervals.transitionInterval = startTransitionProgress(
      progressState,
      98,
      98.5,
      50,
      onProgress
    );
    await new Promise((resolve) => setTimeout(resolve, 50));

    // Transition 2: 98.5-99%
    currentIntervals.transitionInterval = startTransitionProgress(
      progressState,
      98.5,
      99,
      50,
      onProgress
    );
    await new Promise((resolve) => setTimeout(resolve, 50));

    // Transition 3: 99-99.5%
    currentIntervals.transitionInterval = startTransitionProgress(
      progressState,
      99,
      99.5,
      50,
      onProgress
    );
    await new Promise((resolve) => setTimeout(resolve, 50));

    // Final save phase (99.5-100%)
    intervals = startContinuousProgress(
      progressState,
      99.5,
      100,
      100,
      true,
      onProgress
    );
    if (currentIntervals.transitionInterval) {
      clearInterval(currentIntervals.transitionInterval);
    }
    Object.assign(currentIntervals, intervals);

    // Save the PDF
    pdf.save(`${filename}.pdf`);

    // Ensure we reach 100%
    onProgress?.(
      100,
      progressState.estimatedTotalSize,
      progressState.estimatedTotalSize
    );

    // Cleanup all intervals
    progressState.isGenerating = false;
    stopProgress(currentIntervals);
  } catch (error) {
    progressState.isGenerating = false;
    stopProgress(currentIntervals);

    // Cleanup workers if they exist
    estimateWorker = null;
    worker = null;

    console.error('Error in PDF generation:', error);
    throw error;
  }
};
